/**
 * Weekly Pay Report PDF Generator
 * Generates a clean, professional PDF via the browser's print dialog.
 */

const formatHoursMinutes = (decimalHours) => {
  if (!decimalHours || decimalHours === 0) return '0:00';
  const h = Math.floor(decimalHours);
  const m = Math.round((decimalHours - h) * 60);
  if (m === 60) return `${h + 1}:00`;
  return `${h}:${m.toString().padStart(2, '0')}`;
};

const fmtCurrency = (amount) => {
  if (amount === null || amount === undefined) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(amount);
};

/**
 * Generate weekly pay report HTML for printing/PDF
 */
export function generateWeeklyPayHtml({ weeks, grandTotalHours, grandGrossPay, dateRangeLabel }) {
  const employeeCount = new Set(weeks.flatMap(w => w.employees.map(e => e.id))).size;
  const generatedAt = new Date().toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });

  const weekSections = weeks.map((week, idx) => {
    const employeeRows = week.employees.map(emp => {
      const rateRows = emp.rateBreakdown.map((rb, rbIdx) => `
        <tr class="rate-row">
          <td class="indent">${rbIdx === 0 ? `<strong>${emp.name}</strong>` : ''}</td>
          <td class="role-cell">${rb.label}</td>
          <td class="right">${fmtCurrency(rb.rate)}</td>
          <td class="right">${formatHoursMinutes(rb.hours)}</td>
          <td class="right">${fmtCurrency(rb.pay)}</td>
          <td class="right emp-total">${rbIdx === 0 ? formatHoursMinutes(emp.totalHours) : ''}</td>
          <td class="right emp-total">${rbIdx === 0 ? `<strong>${fmtCurrency(emp.grossPay)}</strong>` : ''}</td>
        </tr>
      `).join('');
      return rateRows;
    }).join('');

    return `
      <div class="week-section ${idx > 0 ? 'page-break' : ''}">
        <div class="week-header">
          <div class="week-label">${week.label}</div>
          <div class="week-summary">
            <span class="week-stat">${formatHoursMinutes(week.totalHours)} hrs</span>
            <span class="week-stat-divider">&middot;</span>
            <span class="week-stat bold">${fmtCurrency(week.grossPay)}</span>
          </div>
        </div>
        ${week.employees.length === 0 ? '<p class="no-data">No hours recorded this week.</p>' : `
        <table>
          <thead>
            <tr>
              <th>Employee</th>
              <th>Role</th>
              <th class="right">Rate</th>
              <th class="right">Hours</th>
              <th class="right">Pay</th>
              <th class="right">Total Hrs</th>
              <th class="right">Total Pay</th>
            </tr>
          </thead>
          <tbody>
            ${employeeRows}
          </tbody>
          <tfoot>
            <tr class="week-total-row">
              <td colspan="5"><strong>Week Total</strong></td>
              <td class="right"><strong>${formatHoursMinutes(week.totalHours)}</strong></td>
              <td class="right"><strong>${fmtCurrency(week.grossPay)}</strong></td>
            </tr>
          </tfoot>
        </table>
        `}
      </div>
    `;
  }).join('');

  const html = `
<!DOCTYPE html>
<html>
<head>
  <title>Weekly Pay Report</title>
  <style>
    @page {
      size: letter landscape;
      margin: 0.5in 0.6in;
    }
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: -apple-system, 'Segoe UI', Roboto, Arial, sans-serif;
      color: #1e293b;
      font-size: 11px;
      line-height: 1.5;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    /* Header */
    .report-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      padding-bottom: 16px;
      border-bottom: 3px solid #1a3a2a;
      margin-bottom: 20px;
    }
    .brand {
      display: flex;
      align-items: center;
      gap: 10px;
    }
    .brand-icon {
      width: 36px;
      height: 36px;
      background: #1a3a2a;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #fff;
      font-size: 18px;
    }
    .brand-name {
      font-size: 17px;
      font-weight: 700;
      color: #1a3a2a;
      letter-spacing: -0.3px;
    }
    .brand-sub {
      font-size: 10px;
      color: #64748b;
      text-transform: uppercase;
      letter-spacing: 1.2px;
    }
    .report-title-block {
      text-align: right;
    }
    .report-title {
      font-size: 18px;
      font-weight: 700;
      color: #1a3a2a;
      letter-spacing: -0.3px;
    }
    .report-dates {
      font-size: 11px;
      color: #64748b;
      margin-top: 2px;
    }

    /* KPI strip */
    .kpi-strip {
      display: flex;
      gap: 0;
      margin-bottom: 22px;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      overflow: hidden;
    }
    .kpi {
      flex: 1;
      padding: 12px 16px;
      border-right: 1px solid #e2e8f0;
    }
    .kpi:last-child { border-right: none; }
    .kpi.primary {
      background: #1a3a2a;
      color: #fff;
    }
    .kpi-value {
      font-size: 18px;
      font-weight: 700;
      letter-spacing: -0.5px;
    }
    .kpi.primary .kpi-value { color: #fff; }
    .kpi-label {
      font-size: 9px;
      text-transform: uppercase;
      letter-spacing: 1px;
      color: #94a3b8;
      margin-top: 1px;
    }
    .kpi.primary .kpi-label { color: rgba(255,255,255,0.65); }

    /* Week sections */
    .week-section { margin-bottom: 24px; }
    .page-break { page-break-before: auto; }
    .week-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      background: #f8faf9;
      border: 1px solid #e2e8f0;
      border-bottom: 2px solid #1a3a2a;
      border-radius: 6px 6px 0 0;
      padding: 8px 14px;
    }
    .week-label {
      font-size: 13px;
      font-weight: 700;
      color: #1a3a2a;
    }
    .week-summary {
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .week-stat {
      font-size: 12px;
      color: #475569;
    }
    .week-stat.bold {
      font-weight: 700;
      color: #1a3a2a;
    }
    .week-stat-divider {
      color: #cbd5e1;
    }

    /* Tables */
    table {
      width: 100%;
      border-collapse: collapse;
      border: 1px solid #e2e8f0;
      border-top: none;
    }
    thead tr {
      background: #f1f5f9;
    }
    th {
      padding: 6px 10px;
      font-size: 9px;
      text-transform: uppercase;
      letter-spacing: 0.7px;
      color: #64748b;
      font-weight: 600;
      border-bottom: 1px solid #e2e8f0;
      text-align: left;
    }
    th.right { text-align: right; }
    td {
      padding: 7px 10px;
      font-size: 11px;
      border-bottom: 1px solid #f1f5f9;
    }
    td.right { text-align: right; }
    td.indent { padding-left: 14px; }
    td.emp-total {
      color: #334155;
      background: #fafbfc;
    }
    .role-cell {
      color: #64748b;
      font-size: 10px;
    }
    .rate-row:hover td { background: #f8fafc; }
    tbody tr:last-child td { border-bottom: 1px solid #e2e8f0; }

    /* Week total */
    .week-total-row td {
      background: #f8faf9;
      border-top: 2px solid #1a3a2a;
      padding: 8px 10px;
      font-size: 11px;
    }

    /* Grand totals */
    .grand-totals {
      margin-top: 8px;
      border: 2px solid #1a3a2a;
      border-radius: 8px;
      overflow: hidden;
    }
    .grand-totals-header {
      background: #1a3a2a;
      color: #fff;
      padding: 10px 16px;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
    .grand-totals-header .label {
      font-size: 13px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
    .grand-totals-header .values {
      display: flex;
      gap: 24px;
    }
    .grand-totals-header .val {
      text-align: right;
    }
    .grand-totals-header .val-number {
      font-size: 16px;
      font-weight: 700;
    }
    .grand-totals-header .val-label {
      font-size: 9px;
      text-transform: uppercase;
      letter-spacing: 0.8px;
      opacity: 0.7;
    }

    /* Footer */
    .report-footer {
      margin-top: 24px;
      padding-top: 10px;
      border-top: 1px solid #e2e8f0;
      display: flex;
      justify-content: space-between;
      font-size: 9px;
      color: #94a3b8;
    }

    .no-data {
      padding: 16px;
      text-align: center;
      color: #94a3b8;
      font-style: italic;
      border: 1px solid #e2e8f0;
      border-top: none;
    }

    @media print {
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .week-section { page-break-inside: avoid; }
    }
  </style>
</head>
<body>
  <!-- Header -->
  <div class="report-header">
    <div class="brand">
      <div class="brand-icon">\u2728</div>
      <div>
        <div class="brand-name">Two Sisters</div>
        <div class="brand-sub">Cleaning &amp; Organizing</div>
      </div>
    </div>
    <div class="report-title-block">
      <div class="report-title">Weekly Pay Report</div>
      <div class="report-dates">${dateRangeLabel}</div>
    </div>
  </div>

  <!-- KPI Strip -->
  <div class="kpi-strip">
    <div class="kpi primary">
      <div class="kpi-value">${fmtCurrency(grandGrossPay)}</div>
      <div class="kpi-label">Total Gross Pay</div>
    </div>
    <div class="kpi">
      <div class="kpi-value">${formatHoursMinutes(grandTotalHours)}</div>
      <div class="kpi-label">Total Hours</div>
    </div>
    <div class="kpi">
      <div class="kpi-value">${weeks.length}</div>
      <div class="kpi-label">${weeks.length === 1 ? 'Week' : 'Weeks'}</div>
    </div>
    <div class="kpi">
      <div class="kpi-value">${employeeCount}</div>
      <div class="kpi-label">${employeeCount === 1 ? 'Employee' : 'Employees'}</div>
    </div>
  </div>

  <!-- Week Sections -->
  ${weekSections}

  <!-- Grand Totals -->
  ${weeks.length > 0 ? `
  <div class="grand-totals">
    <div class="grand-totals-header">
      <span class="label">Grand Total</span>
      <div class="values">
        <div class="val">
          <div class="val-number">${formatHoursMinutes(grandTotalHours)}</div>
          <div class="val-label">Hours</div>
        </div>
        <div class="val">
          <div class="val-number">${fmtCurrency(grandGrossPay)}</div>
          <div class="val-label">Gross Pay</div>
        </div>
      </div>
    </div>
  </div>
  ` : ''}

  <!-- Footer -->
  <div class="report-footer">
    <span>Two Sisters Cleaning &amp; Organizing &mdash; Confidential Payroll Document</span>
    <span>Generated ${generatedAt}</span>
  </div>
</body>
</html>
  `;

  return html;
}

/**
 * Open weekly pay report in a new window for printing/saving as PDF
 */
export function openWeeklyPayPdf(reportData) {
  const html = generateWeeklyPayHtml(reportData);
  const printWindow = window.open('', '_blank', 'width=1100,height=800');
  if (printWindow) {
    printWindow.document.write(html);
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
    }, 500);
  }
  return html;
}
